import React, { useEffect, useState } from 'react'
import { Options } from '../start-recording.page'
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import globalClasses from '../../styles.module.scss'
import Modal from '../../../components/modal/modal.component';
import { useTranslation } from 'react-i18next';
import { Meet, Teams, Zoom } from '../../../assets/pages';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService, BotPlatform, BotSessionResponse } from '../../../services/api.service';
import {
  botSessionStatusText,
  checkMeetingUrl,
  isBotSessionFailed,
  isBotSessionTerminal,
  upsertBotSession,
} from './bot-connect.helpers';
import BotSessionList from './bot-session-list.component';

const cn = createCn('bot-connect-modal', classes);

const PLATFORM_ICON: Record<BotPlatform, React.ReactNode> = {
  meet: <Meet />,
  zoom: <Zoom />,
  teams: <Teams />,
};

const POLL_INTERVAL_MS = 4000;
const RECENT_LIMIT = 5;

type ScheduleMode = 'now' | 'scheduled';
type Stage = 'form' | 'status';

/** Modals только маршрутизирует сюда 'zoom' | 'teams' | 'meet' — 'file' уходит в FileModal. */
export type BotConnectPlatform = Exclude<Options, 'file'>;

interface IProps {
  show: boolean;
  setSelectedOption: (option: Options | null) => void;
  type: BotConnectPlatform;
}

/** datetime-local отдаёт "YYYY-MM-DDTHH:mm" — шлём бэкенду как naive datetime с секундами,
 *  без конвертации в UTC (тот же приём, что и due_date у задач: локальное время как есть). */
function toScheduledAtPayload(localValue: string): string | undefined {
  if (!localValue) return undefined;
  return localValue.length === 16 ? `${localValue}:00` : localValue;
}

function nowLocalDatetimeValue(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

const BotConnectModal = ({ show, setSelectedOption, type }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('form');
  const [link, setLink] = useState('');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [session, setSession] = useState<BotSessionResponse | null>(null);
  const [recentSessions, setRecentSessions] = useState<BotSessionResponse[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const resetForm = () => {
    setStage('form');
    setLink('');
    setScheduleMode('now');
    setScheduledAtLocal('');
    setSubmitting(false);
    setFormError(null);
    setSession(null);
  };

  // Открыли модалку заново (в т.ч. на другую платформу) — форма с чистого листа.
  useEffect(() => {
    if (show) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, type]);

  // Видимость сессий (задача F2 п.3): последние 5 моих бот-сессий, подгружаются при открытии.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setRecentLoading(true);
    apiService.listBotSessions()
      .then((list) => {
        if (!cancelled) setRecentSessions(list.slice(0, RECENT_LIMIT));
      })
      .catch(() => {
        // Второстепенная информация под формой — тихо игнорируем сбой загрузки.
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });
    return () => { cancelled = true; };
  }, [show]);

  // Живой поллинг статуса активной сессии каждые 4с, пока статус не финальный.
  useEffect(() => {
    if (stage !== 'status' || !session || isBotSessionTerminal(session.status)) {
      return undefined;
    }
    const sessionId = session.id;
    const intervalId = window.setInterval(() => {
      apiService.getBotSession(sessionId)
        .then((updated) => {
          setSession(updated);
          setRecentSessions((prev) => upsertBotSession(prev, updated, RECENT_LIMIT));
        })
        .catch(() => {
          // Сеть моргнула — подхватим на следующем тике, статус-экран не рушим.
        });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [stage, session]);

  const handleClose = () => {
    // Бот продолжает работать на бэкенде вне зависимости от модалки — предупреждаем об этом,
    // только если сессия реально ещё в процессе (задача F2 п.2).
    if (session && !isBotSessionTerminal(session.status)) {
      toast.info(t('bot-connect.close-toast'));
    }
    resetForm();
    setSelectedOption(null);
  };

  const handleOpenMeeting = (meetingId: string) => {
    resetForm();
    setSelectedOption(null);
    navigate(`/files/${meetingId}`);
  };

  const urlCheck = checkMeetingUrl(type, link);
  const canSubmit = Boolean(link.trim())
    && !(scheduleMode === 'scheduled' && !scheduledAtLocal)
    && !submitting;

  const handleSubmit = async () => {
    const trimmed = link.trim();
    if (!trimmed || !canSubmit) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const created = await apiService.createBotSession({
        platform: type,
        meeting_url: trimmed,
        scheduled_at: scheduleMode === 'scheduled' ? toScheduledAtPayload(scheduledAtLocal) : undefined,
      });
      setSession(created);
      setRecentSessions((prev) => upsertBotSession(prev, created, RECENT_LIMIT));
      setStage('status');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('bot-connect.submit-error');
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <div className={`${cn('heading')} ${classes['modal-heading']}`}>
      {PLATFORM_ICON[type]}
      {t(`bot-connect.heading-${type}`)}
    </div>
  );

  const renderForm = () => (
    <>
      <div className={cn('description')}>
        <p>{t('bot-connect.line1')}</p>
        <p>{t('bot-connect.line2')}</p>
      </div>

      <div className={globalClasses['form-group']}>
        <label htmlFor={`${type}-bot-link`}>{t('bot-connect.link-label')}</label>
        <input
          id={`${type}-bot-link`}
          type="text"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t(`bot-connect.link-placeholder-${type}`) || ''}
          disabled={submitting}
        />
        {urlCheck === 'unknown-domain' && (
          <p className={cn('hint', { warning: true })}>{t('bot-connect.link-hint-mismatch')}</p>
        )}
        {urlCheck === 'not-a-url' && (
          <p className={cn('hint', { warning: true })}>{t('bot-connect.link-hint-invalid')}</p>
        )}
      </div>

      <div className={globalClasses['form-group']}>
        <label>{t('bot-connect.when-label')}</label>
        <div className={cn('segmented')}>
          <button
            type="button"
            className={cn('segmented-option', { active: scheduleMode === 'now' })}
            onClick={() => setScheduleMode('now')}
            disabled={submitting}
          >
            {t('bot-connect.when-now')}
          </button>
          <button
            type="button"
            className={cn('segmented-option', { active: scheduleMode === 'scheduled' })}
            onClick={() => setScheduleMode('scheduled')}
            disabled={submitting}
          >
            {t('bot-connect.when-scheduled')}
          </button>
        </div>

        {scheduleMode === 'scheduled' && (
          <>
            <input
              type="datetime-local"
              value={scheduledAtLocal}
              min={nowLocalDatetimeValue()}
              onChange={(e) => setScheduledAtLocal(e.target.value)}
              disabled={submitting}
            />
            <p className={cn('hint')}>{t('bot-connect.scheduled-hint')}</p>
          </>
        )}
      </div>

      {formError && <p className={cn('error-text')}>{formError}</p>}
    </>
  );

  const renderStatus = () => {
    if (!session) return null;
    const failed = isBotSessionFailed(session.status);
    const terminal = isBotSessionTerminal(session.status);

    return (
      <>
        <div className={cn('status-card', { failed, done: terminal && !failed })}>
          <span className={cn('status-text')}>{botSessionStatusText(t, session)}</span>
          {session.status === 'joining' && (
            <span className={cn('status-hint')}>{t('bot-connect.status-joining-hint')}</span>
          )}
        </div>

        {session.meeting_id && (
          <button
            type="button"
            className={cn('open-meeting-btn')}
            onClick={() => handleOpenMeeting(session.meeting_id as string)}
          >
            {t('bot-connect.status-done-link')}
          </button>
        )}

        {terminal && (
          <button type="button" className={cn('retry-btn')} onClick={resetForm}>
            {t('bot-connect.back-to-form')}
          </button>
        )}
      </>
    );
  };

  const footer = stage === 'form'
    ? (
      <div className={classes['modal-footer']}>
        <button onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? t('bot-connect.submitting') : t('bot-connect.submit')}
        </button>
      </div>
    )
    : undefined;

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      style={{ padding: 0, borderRadius: '1.25rem', width: '34rem', maxWidth: 'min(34rem, 95vw)' }}
      header={header}
      footer={footer}
    >
      <div className={classes['modal-body']}>
        {stage === 'form' ? renderForm() : renderStatus()}
      </div>

      <BotSessionList
        sessions={recentSessions}
        loading={recentLoading}
        onOpenMeeting={handleOpenMeeting}
      />
    </Modal>
  )
}

export default BotConnectModal
